import PDFDocument from 'pdfkit';
import { formatMoney } from '@roovers/shared';
import { csvLine } from '../csv/csv.parser.js';
import type {
  BalanceSheet,
  CashFlow,
  IncomeStatement,
  Statement,
  VatSummary,
  WhtReport,
} from './reports.service.js';

export interface ReportTable {
  title: string;
  subtitle: string;
  columns: { label: string; right?: boolean }[];
  rows: { cells: string[]; bold?: boolean; indent?: boolean }[];
  footnote?: string;
}

const m = (v: string) => formatMoney(v, 'UGX');
const dmy = (d: string) => d.split('-').reverse().join('/');

export function incomeTable(r: IncomeStatement): ReportTable {
  const cmp = !!r.comparePeriod;
  const cols = [
    { label: 'Account' },
    { label: 'Amount', right: true },
    ...(cmp ? [{ label: 'Compared with', right: true }] : []),
  ];
  const row = (label: string, a: string, c?: string, bold = false, indent = false) => ({
    cells: [label, m(a), ...(cmp ? [m(c ?? '0')] : [])],
    bold,
    indent,
  });
  const rows: ReportTable['rows'] = [];
  const sec = (
    title: string,
    lines: IncomeStatement['sections']['revenue'],
    total: { amount: string; compare?: string },
  ) => {
    rows.push({ cells: [title, '', ...(cmp ? [''] : [])], bold: true });
    for (const l of lines) rows.push(row(`${l.code} ${l.name}`, l.amount, l.compare, false, true));
    rows.push(row(`Total ${title.toLowerCase()}`, total.amount, total.compare, true));
  };
  const t = r.totals;
  sec('Revenue', r.sections.revenue, t.revenue);
  sec('Cost of sales', r.sections.costOfSales, t.costOfSales);
  rows.push(row('Gross profit', t.grossProfit.amount, t.grossProfit.compare, true));
  sec('Operating expenses', r.sections.operatingExpenses, t.operatingExpenses);
  rows.push(row('Operating profit', t.operatingProfit.amount, t.operatingProfit.compare, true));
  sec('Other income', r.sections.otherIncome, t.otherIncome);
  sec('Other expenses', r.sections.otherExpenses, t.otherExpenses);
  rows.push(row('Net profit before tax', t.netProfit.amount, t.netProfit.compare, true));
  return {
    title: 'Income Statement',
    subtitle: `${dmy(r.period.from)} to ${dmy(r.period.to)} (accrual basis)${r.comparePeriod ? `, compared with ${dmy(r.comparePeriod.from)} to ${dmy(r.comparePeriod.to)}` : ''}`,
    columns: cols,
    rows,
  };
}

export function balanceTable(r: BalanceSheet): ReportTable {
  const rows: ReportTable['rows'] = [];
  const side = (title: string, groups: BalanceSheet['assets'], total: string) => {
    rows.push({ cells: [title, ''], bold: true });
    for (const g of groups) {
      rows.push({ cells: [g.group, m(g.total)], bold: true, indent: true });
      for (const l of g.lines)
        rows.push({ cells: [`${l.code} ${l.name}`, m(l.amount)], indent: true });
    }
    rows.push({ cells: [`Total ${title.toLowerCase()}`, m(total)], bold: true });
  };
  side('Assets', r.assets, r.totals.assets);
  side('Liabilities', r.liabilities, r.totals.liabilities);
  rows.push({ cells: ['Equity', ''], bold: true });
  for (const l of r.equity.lines)
    rows.push({ cells: [`${l.code} ${l.name}`, m(l.amount)], indent: true });
  rows.push({ cells: ['Current period earnings', m(r.equity.currentEarnings)], indent: true });
  rows.push({ cells: ['Total equity', m(r.equity.total)], bold: true });
  rows.push({
    cells: ['Total liabilities and equity', m(r.totals.liabilitiesAndEquity)],
    bold: true,
  });
  return {
    title: 'Balance Sheet',
    subtitle: `As of ${dmy(r.asOf)}`,
    columns: [{ label: 'Account' }, { label: 'Amount', right: true }],
    rows,
    footnote: r.balanced
      ? 'Assets equal liabilities plus equity.'
      : 'WARNING: the balance sheet does not balance.',
  };
}

export function cashTable(r: CashFlow): ReportTable {
  const rows: ReportTable['rows'] = [{ cells: ['Opening cash', m(r.openingCash)], bold: true }];
  const sec = (title: string, lines: CashFlow['sections']['operating'], total: string) => {
    rows.push({ cells: [title, ''], bold: true });
    for (const l of lines) rows.push({ cells: [l.label, m(l.amount)], indent: true });
    rows.push({
      cells: [`Net cash from ${title.toLowerCase().replace(' activities', '')}`, m(total)],
      bold: true,
    });
  };
  sec('Operating activities', r.sections.operating, r.totals.operating);
  sec('Investing activities', r.sections.investing, r.totals.investing);
  sec('Financing activities', r.sections.financing, r.totals.financing);
  rows.push({ cells: ['Net change in cash', m(r.totals.netChange)], bold: true });
  rows.push({ cells: ['Closing cash', m(r.closingCash)], bold: true });
  return {
    title: 'Cash Flow Statement',
    subtitle: `${dmy(r.period.from)} to ${dmy(r.period.to)} (direct method, all bank, mobile money and cash accounts, in UGX)`,
    columns: [{ label: 'Item' }, { label: 'Amount', right: true }],
    rows,
    footnote: r.reconciled
      ? `Closing cash equals the sum of all cash account balances (${m(r.cashAccountsBalance)}).`
      : `WARNING: closing cash differs from the cash accounts (${m(r.cashAccountsBalance)}).`,
  };
}

export function vatTable(r: VatSummary): ReportTable {
  const rows: ReportTable['rows'] = [{ cells: ['Sales (output VAT)', '', ''], bold: true }];
  for (const s of r.sales) rows.push({ cells: [s.vatCode, m(s.net), m(s.vat)], indent: true });
  rows.push({ cells: ['Total output VAT', '', m(r.outputVat)], bold: true });
  rows.push({ cells: ['Purchases and expenses (input VAT)', '', ''], bold: true });
  for (const s of r.purchases) rows.push({ cells: [s.vatCode, m(s.net), m(s.vat)], indent: true });
  rows.push({ cells: ['Total input VAT', '', m(r.inputVat)], bold: true });
  rows.push({
    cells: ['VAT payable to URA (output minus input)', '', m(r.netVatPayable)],
    bold: true,
  });
  return {
    title: 'VAT Return Summary',
    subtitle: `${dmy(r.period.from)} to ${dmy(r.period.to)}`,
    columns: [
      { label: 'Category' },
      { label: 'Net amount', right: true },
      { label: 'VAT', right: true },
    ],
    rows,
    footnote: r.reconciled
      ? 'Matches the VAT accounts in the ledger.'
      : 'WARNING: differs from the VAT accounts in the ledger for this period.',
  };
}

export function whtTable(r: WhtReport): ReportTable {
  const rows: ReportTable['rows'] = [
    { cells: ['Withheld by us from vendors (owed to URA)', '', '', ''], bold: true },
  ];
  for (const p of r.withheldByUs)
    rows.push({ cells: [p.payoutNo, dmy(p.date), p.vendor, m(p.base)], indent: true });
  rows.push({ cells: ['Total withheld by us', '', '', m(r.totals.withheldByUs)], bold: true });
  rows.push({
    cells: ['Withheld from us by clients (credit against our tax)', '', '', ''],
    bold: true,
  });
  for (const p of r.withheldFromUs)
    rows.push({ cells: [p.receiptNo, dmy(p.date), p.client, m(p.base)], indent: true });
  rows.push({ cells: ['Total withheld from us', '', '', m(r.totals.withheldFromUs)], bold: true });
  return {
    title: 'Withholding Tax Report',
    subtitle: `${dmy(r.period.from)} to ${dmy(r.period.to)}`,
    columns: [
      { label: 'Number' },
      { label: 'Date' },
      { label: 'Party' },
      { label: 'Amount (UGX)', right: true },
    ],
    rows,
  };
}

export function statementTable(r: Statement): ReportTable {
  const money = (v: string) => (v ? formatMoney(v, r.currency) : '');
  const rows: ReportTable['rows'] = [
    { cells: ['', 'Opening balance', '', '', '', money(r.opening)], bold: true },
  ];
  for (const x of r.rows)
    rows.push({
      cells: [
        dmy(x.date),
        `${x.type} ${x.number}`.trim(),
        x.description,
        money(x.debit),
        money(x.credit),
        money(x.balance),
      ],
    });
  rows.push({
    cells: [
      '',
      'Closing balance',
      '',
      money(r.totalDebits),
      money(r.totalCredits),
      money(r.closing),
    ],
    bold: true,
  });
  const client = r.kind === 'client';
  return {
    title: client ? 'Client Statement' : 'Vendor Statement',
    subtitle: `${r.party.name}${r.party.tin ? ` (TIN ${r.party.tin})` : ''} · ${dmy(r.period.from)} to ${dmy(r.period.to)} · ${r.currency}`,
    columns: [
      { label: 'Date' },
      { label: 'Document' },
      { label: 'Details' },
      { label: client ? 'Charged' : 'Paid', right: true },
      { label: client ? 'Received' : 'Billed', right: true },
      { label: client ? 'Balance owed to us' : 'Balance we owe', right: true },
    ],
    rows,
  };
}

/** CSV with formula neutralisation (csvLine) and a UTF-8 BOM so Excel opens it correctly. */
export function tableToCsv(t: ReportTable): string {
  const lines = [csvLine([t.title]), csvLine([t.subtitle]), csvLine(t.columns.map((c) => c.label))];
  // Amounts become plain numbers (no currency word or thousands commas) so spreadsheets can sum them.
  const asNumber = (c: string) =>
    /^[A-Z]{3}\s-?[\d,]+(\.\d+)?$/.test(c) ? c.replace(/^[A-Z]{3}\s/, '').replace(/,/g, '') : c;
  for (const r of t.rows) lines.push(csvLine(r.cells.map(asNumber)));
  if (t.footnote) lines.push(csvLine([t.footnote]));
  return '﻿' + lines.join('\r\n') + '\r\n';
}

const MARGIN = 40;

export function tableToPdf(
  t: ReportTable,
  company: { name: string; tin: string | null },
): Promise<Buffer> {
  return new Promise((resolve, reject) => {
    const doc = new PDFDocument({
      size: 'A4',
      margin: MARGIN,
      compress: process.env.NODE_ENV !== 'test',
    });
    const chunks: Buffer[] = [];
    doc.on('data', (c: Buffer) => chunks.push(c));
    doc.on('end', () => resolve(Buffer.concat(chunks)));
    doc.on('error', reject);
    const width = 595.28 - MARGIN * 2;
    const n = t.columns.length;
    // First column gets the remaining space; the numeric columns are fixed width.
    const numW = n <= 3 ? 110 : 80;
    const colW: number[] = t.columns.map((c, i) =>
      i === 0 ? 0 : c.right ? numW : n > 4 ? (i === 2 ? 110 : 60) : 90,
    );
    colW[0] = width - colW.slice(1).reduce((s, x) => s + x, 0);
    const header = () => {
      doc.font('Helvetica-Bold').fontSize(14).fillColor('#111').text(company.name, MARGIN, MARGIN);
      doc
        .font('Helvetica')
        .fontSize(9)
        .fillColor('#555')
        .text(company.tin ? `TIN ${company.tin}` : '', MARGIN, doc.y);
      doc.moveDown(0.5).font('Helvetica-Bold').fontSize(13).fillColor('#111').text(t.title);
      doc.font('Helvetica').fontSize(9).fillColor('#555').text(t.subtitle, { width });
      doc.moveDown(0.6);
    };
    const drawRow = (
      cells: string[],
      opts: { bold?: boolean; indent?: boolean; head?: boolean },
    ) => {
      const y = doc.y;
      if (y > 780) {
        doc.addPage();
        header();
      }
      let x = MARGIN;
      const top = doc.y;
      let h = 0;
      cells.forEach((cell, i) => {
        doc
          .font(opts.bold || opts.head ? 'Helvetica-Bold' : 'Helvetica')
          .fontSize(9)
          .fillColor(opts.head ? '#fff' : '#111');
        const pad = i === 0 && opts.indent ? 12 : 0;
        const before = doc.y;
        doc.text(cell, x + 3 + pad, top + 2, {
          width: colW[i]! - 6 - pad,
          align: t.columns[i]!.right ? 'right' : 'left',
        });
        h = Math.max(h, doc.y - top);
        doc.y = before;
        x += colW[i]!;
      });
      if (opts.head) {
        // Header band is painted before the text in most PDF viewers only if drawn first; keep it simple with a rule.
      }
      doc.y = top + h + 2;
      doc
        .moveTo(MARGIN, doc.y)
        .lineTo(MARGIN + width, doc.y)
        .strokeColor('#ddd')
        .lineWidth(0.5)
        .stroke();
      doc.y += 2;
    };
    header();
    // Header row on a coloured band.
    const bandY = doc.y;
    doc.rect(MARGIN, bandY, width, 16).fill('#CC0000');
    doc.y = bandY;
    drawRow(
      t.columns.map((c) => c.label),
      { head: true },
    );
    for (const r of t.rows) drawRow(r.cells, r);
    if (t.footnote)
      doc
        .moveDown(0.6)
        .font('Helvetica-Oblique')
        .fontSize(8)
        .fillColor('#555')
        .text(t.footnote, MARGIN, doc.y, { width });
    doc.end();
  });
}
